import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react"

/** `key` is the owning {@link InspectorDrilldown}'s `useId()`, so a re-open finds its level. */
type DrilldownLevel = {
  key: string
  title?: string
  minWidth?: number
  expandableAt?: number
  /** Null until its view has mounted. */
  container: HTMLElement | null
}

type InspectorDrilldownContextValue = {
  /** Outermost first; empty = the inspector's own content. */
  stack: DrilldownLevel[]
  /** Push a level, or return to it if it is already open. */
  open: (
    key: string,
    title: string | undefined,
    minWidth: number | undefined,
    expandableAt: number | undefined,
  ) => void
  back: () => void
  close: () => void
  remove: (key: string) => void
  setContainer: (key: string, el: HTMLElement | null) => void
}

const InspectorDrilldownContext = createContext<InspectorDrilldownContextValue | null>(null)

export const InspectorDrilldownProvider = ({ children }: { children: ReactNode }) => {
  // State, not a ref: consumers portal into each level's container, so they must re-render once
  // it exists.
  const [stack, setStack] = useState<DrilldownLevel[]>([])

  const open = useCallback(
    (
      key: string,
      title: string | undefined,
      minWidth: number | undefined,
      expandableAt: number | undefined,
    ) =>
      setStack((prev) => {
        // Returns to an already-open level rather than stacking a duplicate.
        const existing = prev.findIndex((level) => level.key === key)
        if (existing !== -1) {
          return existing === prev.length - 1 ? prev : prev.slice(0, existing + 1)
        }
        return [...prev, { key, title, minWidth, expandableAt, container: null }]
      }),
    [],
  )
  const back = useCallback(() => setStack((prev) => prev.slice(0, -1)), [])
  const close = useCallback(() => setStack((prev) => (prev.length === 0 ? prev : [])), [])
  const remove = useCallback(
    (key: string) =>
      setStack((prev) => {
        const index = prev.findIndex((level) => level.key === key)
        return index === -1 ? prev : prev.slice(0, index)
      }),
    [],
  )
  const setContainer = useCallback(
    (key: string, el: HTMLElement | null) =>
      setStack((prev) => {
        const index = prev.findIndex((level) => level.key === key)
        // Keep `prev` when nothing changes: a new array re-renders every level, and a detach/attach
        // pair on the same node would never settle.
        if (index === -1 || prev[index]?.container === el) return prev
        const next = [...prev]
        next[index] = { ...next[index]!, container: el }
        return next
      }),
    [],
  )

  const value = useMemo<InspectorDrilldownContextValue>(
    () => ({ stack, open, back, close, remove, setContainer }),
    [stack, open, back, close, remove, setContainer],
  )
  return (
    <InspectorDrilldownContext.Provider value={value}>
      {children}
    </InspectorDrilldownContext.Provider>
  )
}

/** Internal — read by ModuleInspector and {@link InspectorDrilldown}. */
export const useInspectorDrilldownInternal = () => {
  const ctx = useContext(InspectorDrilldownContext)
  if (!ctx) {
    throw new Error("useInspectorDrilldownInternal must be used inside a <Module>")
  }
  return ctx
}

/**
 * Drilldown state and exits (`back` pops one level, `close` clears all). To OPEN one, render an
 * `<InspectorDrilldown>`. Use only inside inspector content.
 */
export const useInspectorDrilldown = () => {
  const { close, back, stack } = useInspectorDrilldownInternal()
  return { close, back, isOpen: stack.length > 0, depth: stack.length }
}

/** The px floor the open stack asks of the inspector — the deepest `minWidth` wins. */
export const useIsDrilldownOpen = () => useInspectorDrilldownInternal().stack.length > 0

export const useDrilldownMinWidth = () => {
  const { stack } = useInspectorDrilldownInternal()
  return stack.reduce((widest, level) => Math.max(widest, level.minWidth ?? 0), 0)
}

/**
 * The top level's `expandableAt` (0 = none) and key, so an expansion is tied to that level and
 * can't outlive it. Top only: unlike the cumulative `minWidth`, expand belongs to the visible view.
 */
export const useDrilldownExpandableAt = () => {
  const { stack } = useInspectorDrilldownInternal()
  const top = stack.at(-1)
  return useMemo(
    () => ({ expandableAt: top?.expandableAt ?? 0, levelKey: top?.key ?? null }),
    [top?.expandableAt, top?.key],
  )
}
